import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import LoadingSpinner from "../../loadingSpinner";
import { tokens } from "../../theme";
import {
  getOrdenCargaById,
  patchOrdenCargaStatus,
} from "../../services/ordenCarga.services";

const STATUS_OPTIONS = [
  "AGUARDANDO_CARREGAMENTO",
  "EN_CARGA",
  "CERRADA",
  "ANULADA",
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const OrdenCargaDetalle = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();

  const [orden, setOrden] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [statusEditable, setStatusEditable] = useState("AGUARDANDO_CARREGAMENTO");
  const [expandedItemId, setExpandedItemId] = useState(null);

  const fetchDetalle = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const data = await getOrdenCargaById(id);
      setOrden(data || null);
      setStatusEditable(data?.status || "AGUARDANDO_CARREGAMENTO");
    } catch (error) {
      toast.error(error.message || "No se pudo obtener el detalle de la orden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetalle();
  }, [id]);

  const pedidosRows = useMemo(() => {
    const rows = Array.isArray(orden?.pedidos) ? orden.pedidos : [];
    return rows.map((pedido, index) => ({
      id: pedido?.id ?? `pedido-${index}`,
      n_pedido: pedido?.id_pedido_venta ?? pedido?.id_pedido ?? "-",
      cliente: pedido?.cliente ?? "-",
      codigo: pedido?.cod ?? pedido?.codigo ?? "-",
    }));
  }, [orden]);

  const placasRows = useMemo(() => {
    const rows = Array.isArray(orden?.placas) ? orden.placas : [];
    return rows.map((placa, index) => ({
      id: placa?.id ?? `placa-${index}`,
      tipo: placa?.tipo || "-",
      placa: placa?.placa || "-",
    }));
  }, [orden]);

  const itemsRows = useMemo(() => {
    const rows = Array.isArray(orden?.items) ? orden.items : [];
    return rows.map((item, index) => ({
      id: item?.id ?? `item-${index}`,
      producto_descripcion: item?.producto_descripcion || "SIN DESCRIPCION",
      espesor_mm: toNumber(item?.espesor_mm),
      nro_paquetes: toNumber(item?.nro_paquetes),
      chapas_por_paquete: toNumber(item?.chapas_por_paquete),
      nro_chapas: toNumber(item?.nro_chapas),
      comp_mm: toNumber(item?.comp_mm),
      altura_mm: toNumber(item?.altura_mm),
      area_m2: toNumber(item?.area_m2),
      peso_kg: toNumber(item?.peso_kg),
      kg_m2: toNumber(item?.kg_m2),
    }));
  }, [orden]);

  const itemExpandido = useMemo(
    () => itemsRows.find((item) => String(item.id) === String(expandedItemId)) || null,
    [itemsRows, expandedItemId]
  );

  const handleGuardarEdicion = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      await patchOrdenCargaStatus(id, statusEditable);
      toast.success("Edicion guardada correctamente");
      setModoEdicion(false);
      await fetchDetalle();
    } catch (error) {
      toast.error(error.message || "No se pudo guardar la edicion");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelarOrden = async () => {
    if (!id) return;

    const confirmed = window.confirm("Confirma la cancelacion de esta orden de carga?");
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await patchOrdenCargaStatus(id, "ANULADA");
      toast.success("Orden cancelada correctamente");
      setStatusEditable("ANULADA");
      setModoEdicion(false);
      await fetchDetalle();
    } catch (error) {
      toast.error(error.message || "No se pudo cancelar la orden");
    } finally {
      setIsLoading(false);
    }
  };

  const pedidosColumns = [
    { field: "id", headerName: "ID", flex: 0.3 },
    { field: "n_pedido", headerName: "Pedido", flex: 0.6 },
    { field: "cliente", headerName: "Cliente", flex: 0.8 },
    { field: "codigo", headerName: "Codigo", flex: 1.5 },
  ];

  const placasColumns = [
    { field: "tipo", headerName: "Tipo", flex: 0.8 },
    { field: "placa", headerName: "Placa", flex: 1 },
  ];

  const itemsColumns = [
    {
      field: "expand",
      headerName: "Detalle",
      width: 90,
      sortable: false,
      renderCell: (params) => {
        const isOpen = String(expandedItemId) === String(params.row.id);
        return (
          <IconButton
            size="small"
            onClick={() => setExpandedItemId(isOpen ? null : params.row.id)}
          >
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        );
      },
    },
    { field: "producto_descripcion", headerName: "Producto", flex: 1.4 },
    { field: "espesor_mm", headerName: "Espesor", flex: 0.5 },
    { field: "nro_paquetes", headerName: "Paquetes", flex: 0.5 },
    { field: "nro_chapas", headerName: "Chapas", flex: 0.5 },
    {
      field: "peso_kg",
      headerName: "Peso (kg)",
      flex: 0.6,
      valueGetter: (params) => toNumber(params.row.peso_kg).toLocaleString("es-PY"),
    },
  ];

  return (
    <Box m="20px">
      {isLoading && <LoadingSpinner />}

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
        <Header
          title="Detalle Orden de Carga"
          subtitle="Lectura, edicion, cancelacion y detalle desplegable de items"
        />

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<VisibilityIcon />}
            onClick={() => setModoEdicion(false)}
          >
            Lectura
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={() => setModoEdicion(true)}
          >
            Edicion
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleCancelarOrden}
          >
            Cancelacion
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/view/ordenCarga")}
          >
            Volver
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[400] }}>
        <Box display="grid" gridTemplateColumns="repeat(4, minmax(130px, 1fr))" gap={1}>
          <Typography><b>ID:</b> {orden?.id || "-"}</Typography>
          <Typography><b>Nro Orden:</b> {orden?.nro_orden || "-"}</Typography>
          <Typography><b>Fecha:</b> {formatDate(orden?.fecha_carga)}</Typography>
          <Typography><b>Motorista:</b> {orden?.motorista_nombre || "-"}</Typography>
          <Typography><b>Tara Kg:</b> {toNumber(orden?.tara_kg)}</Typography>
          <Typography><b>Caballete:</b> {toNumber(orden?.caballete_cantidad)}</Typography>
          <Typography><b>Paquetes:</b> {toNumber(orden?.totales?.total_paquetes ?? orden?.total_paquetes)}</Typography>
          <Typography><b>Chapas:</b> {toNumber(orden?.totales?.total_chapas ?? orden?.total_chapas)}</Typography>
        </Box>

        <Box mt={2} display="flex" alignItems="center" gap={1}>
          <Chip label={`Status actual: ${orden?.status || "-"}`} color="info" />
          <TextField
            select
            size="small"
            label="Status"
            value={statusEditable}
            disabled={!modoEdicion}
            onChange={(event) => setStatusEditable(event.target.value)}
            sx={{ minWidth: "260px", backgroundColor: "white", borderRadius: "4px" }}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            color="success"
            disabled={!modoEdicion}
            startIcon={<SaveIcon />}
            onClick={handleGuardarEdicion}
          >
            Guardar edicion
          </Button>
        </Box>
      </Paper>

      <Box mb={2}>
        <Typography variant="h5" mb={1}>Pedidos vinculados</Typography>
        <Box
          height="30vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[900],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[900],
            },
          }}
        >
          <DataGrid
            rows={pedidosRows}
            columns={pedidosColumns}
            disableSelectionOnClick
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
          />
        </Box>
      </Box>

      {/* <Box mb={2}>
        <Typography variant="h5" mb={1}>Placas</Typography>
        <Box
          height="170px"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[900],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[900],
            },
          }}
        >
          <DataGrid
            rows={placasRows}
            columns={placasColumns}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
          />
        </Box>
      </Box> */}

      <Box>
        <Typography variant="h5" mb={1}>Items (grilla desplegable)</Typography>
        <Box
          height="320px"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[900],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[900],
            },
          }}
        >
          <DataGrid
            rows={itemsRows}
            columns={itemsColumns}
            disableSelectionOnClick
            pageSize={6}
            rowsPerPageOptions={[6, 12, 20]}
          />
        </Box>

        {itemExpandido && (
          <Paper sx={{ mt: 1.5, p: 2, backgroundColor: colors.primary[400] }}>
            <Typography variant="h6" mb={1}>
              Detalle item: {itemExpandido.producto_descripcion}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box display="grid" gridTemplateColumns="repeat(4, minmax(120px, 1fr))" gap={1}>
              <Typography><b>Espesor mm:</b> {itemExpandido.espesor_mm}</Typography>
              <Typography><b>Paquetes:</b> {itemExpandido.nro_paquetes}</Typography>
              <Typography><b>Chapas/paquete:</b> {itemExpandido.chapas_por_paquete}</Typography>
              <Typography><b>Chapas totales:</b> {itemExpandido.nro_chapas}</Typography>
              <Typography><b>Comp mm:</b> {itemExpandido.comp_mm}</Typography>
              <Typography><b>Altura mm:</b> {itemExpandido.altura_mm}</Typography>
              <Typography><b>Area m2:</b> {itemExpandido.area_m2}</Typography>
              <Typography><b>kg/m2:</b> {itemExpandido.kg_m2}</Typography>
              <Typography><b>Peso kg:</b> {itemExpandido.peso_kg}</Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default OrdenCargaDetalle;
